'use client';

import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { createClient } from '@/lib/supabase/client';
import type { Employee, EmployeeTimeOffBalance } from './types';

interface TimeOffRequestModalProps {
  employee: Employee | null;
  balances: EmployeeTimeOffBalance[];
  onClose: () => void;
  onSaved: () => void;
}

const TimeOffRequestModal = ({ employee, balances, onClose, onSaved }: TimeOffRequestModalProps) => {
  const [balanceType, setBalanceType] = useState<string>('pto');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [hoursRequested, setHoursRequested] = useState<number>(8);
  const [notes, setNotes] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const supabase = createClient();

  useEffect(() => {
    if (!employee) return;
    setSubmitting(false);
    setBalanceType('pto');
    const today = new Date().toISOString().slice(0, 10);
    setStartDate(today); setEndDate(today);
    setHoursRequested(8); setNotes('');
  }, [employee?.id]);

  useEffect(() => {
    if (!employee) return;
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [employee, onClose]);

  if (!employee) return null;

  const currentBalance = balances.find(b => b.balance_type === balanceType);
  const remainingHours = currentBalance?.hours_remaining ?? 0;
  const wouldExceedBalance = hoursRequested > remainingHours;
  const isValid = !!startDate && !!endDate && hoursRequested > 0 && !!balanceType && !submitting;

  const submitRequest = async () => {
    if (!isValid || !currentBalance) return;
    if (wouldExceedBalance) {
      if (!confirm(`This request (${hoursRequested}h) exceeds available ${balanceType.toUpperCase()} balance (${remainingHours}h remaining). Continue anyway?`)) return;
    }
    setSubmitting(true);

    const newHoursUsed = (currentBalance.hours_used ?? 0) + hoursRequested;
    const updatedNotes = [
      currentBalance.notes ?? '',
      `${new Date().toISOString().slice(0, 10)}: ${hoursRequested}h requested for ${startDate}${startDate !== endDate ? ` to ${endDate}` : ''}${notes.trim() ? ' — ' + notes.trim() : ''}`,
    ].filter(Boolean).join('\n');

    console.log(`[TimeOff] Submitting: ${hoursRequested}h ${balanceType}`);

    for (let attempt = 0; attempt < 3; attempt++) {
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error(`Save timed out (attempt ${attempt + 1}/3)`)), 8000));
      const opPromise = supabase
        .from('employee_time_off_balances')
        .update({ hours_used: newHoursUsed, notes: updatedNotes, updated_at: new Date().toISOString() })
        .eq('id', currentBalance.id)
        .then((res: any) => { if (res.error) throw res.error; return res.data; });

      try {
        await Promise.race([opPromise, timeoutPromise]);
        console.log(`[TimeOff] Update succeeded attempt ${attempt + 1}`);
        toast.success(`${hoursRequested}h ${balanceType.toUpperCase()} approved`, { duration: 3000, icon: '🌴' });
        onSaved();
        setSubmitting(false);
        onClose();
        return;
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        const isRateLimit = msg.includes('429') || msg.toLowerCase().includes('too many');
        const isTimeout = msg.includes('timed out');
        console.warn(`[TimeOff] Attempt ${attempt + 1} failed: ${msg}`);
        if (attempt === 2 || (!isRateLimit && !isTimeout)) {
          toast.error(`Save failed: ${msg}`, { duration: 6000 });
          setSubmitting(false);
          return;
        }
        await new Promise(r => setTimeout(r, 500 * Math.pow(2, attempt)));
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[120] flex items-center justify-center p-4" onClick={onClose} role="dialog" aria-modal="true">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between p-5 border-b border-gray-100">
          <div>
            <h3 className="text-lg font-bold text-[#1C2E5E]">Request Time Off</h3>
            <p className="text-[12px] text-gray-500 mt-0.5">{employee.first_name} {employee.last_name}</p>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18M6 6l12 12" strokeLinecap="round"/></svg>
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-[11px] font-semibold text-gray-700 mb-1.5">Balance Type</label>
            <div className="grid grid-cols-2 gap-2">
              {balances.map(b => {
                const labels: Record<string, string> = { pto: 'PTO', sick: 'Sick', bereavement: 'Bereavement', jury_duty: 'Jury Duty', parental: 'Parental', floating_holiday: 'Floating', unpaid: 'Unpaid', other: 'Other' };
                const active = balanceType === b.balance_type;
                return (
                  <button key={b.id} type="button" onClick={() => setBalanceType(b.balance_type)}
                    className={`px-3 py-2 rounded-lg text-[12px] font-semibold transition-all ${active ? 'bg-[#E89A3C] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                    {labels[b.balance_type] ?? b.balance_type} · {b.hours_remaining.toFixed(1)}h
                  </button>
                );
              })}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-semibold text-gray-700 mb-1.5">Start Date <span className="text-red-500">*</span></label>
              <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-full px-3 py-2 text-[12px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none transition-all" />
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-gray-700 mb-1.5">End Date <span className="text-red-500">*</span></label>
              <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-full px-3 py-2 text-[12px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none transition-all" />
            </div>
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-gray-700 mb-1.5">Hours Requested <span className="text-red-500">*</span></label>
            <input type="number" min="0.5" step="0.5" value={hoursRequested} onChange={e => setHoursRequested(Number(e.target.value))} className="w-full px-3 py-2 text-[12px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none transition-all" />
            <div className="flex items-center justify-between mt-1.5">
              <p className={`text-[11px] ${wouldExceedBalance ? 'text-red-600 font-bold' : 'text-gray-500'}`}>
                {wouldExceedBalance ? '⚠ Exceeds available balance' : `${(remainingHours - hoursRequested).toFixed(1)}h remaining after this request`}
              </p>
              <button type="button" onClick={() => setHoursRequested(8)} className="text-[10px] text-[#E89A3C] hover:underline">Reset to 8h</button>
            </div>
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-gray-700 mb-1.5">Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} maxLength={500} placeholder="Optional reason or details" className="w-full px-3 py-2 text-[12px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none transition-all resize-none" />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 p-5 border-t border-gray-100">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-[13px] font-semibold text-gray-600 hover:bg-gray-100">Cancel</button>
          <button type="button" onClick={submitRequest} disabled={!isValid} className="px-4 py-2 rounded-lg text-[13px] font-semibold text-white bg-[#1C2E5E] hover:bg-[#022b80] disabled:opacity-50 disabled:cursor-not-allowed">
            {submitting ? 'Submitting…' : 'Approve & Apply'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TimeOffRequestModal;
